/**
 * Production-safe logger utility with log level support
 * Logs in development environment and respects LOG_LEVEL setting
 */

// Detect development environment - works across React Native, Expo Web, and Web
const isDevelopment =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : process.env.NODE_ENV === 'development';

// Log levels: 0=error, 1=warn, 2=info, 3=debug, 4=log
type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'log';

const LOG_LEVEL_MAP: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  log: 4,
};

// Get log level from environment or default to 'debug' in dev, 'error' in prod
const getLogLevel = (): number => {
  if (!isDevelopment) return LOG_LEVEL_MAP.error;

  const envLevel =
    process.env['EXPO_PUBLIC_LOG_LEVEL'] || process.env['LOG_LEVEL'] || 'debug';
  return LOG_LEVEL_MAP[envLevel as LogLevel] ?? LOG_LEVEL_MAP.debug;
};

const currentLogLevel = getLogLevel();

export const logger = {
  log: (...args: any[]) => {
    if (currentLogLevel >= LOG_LEVEL_MAP.log) {
      console.log(...args);
    }
  },
  info: (...args: any[]) => {
    if (currentLogLevel >= LOG_LEVEL_MAP.info) {
      console.info(...args);
    }
  },
  warn: (...args: any[]) => {
    if (currentLogLevel >= LOG_LEVEL_MAP.warn) {
      console.warn(...args);
    }
  },
  error: (...args: any[]) => {
    // Always log errors, even in production
    console.error(...args);
  },
  debug: (...args: any[]) => {
    if (currentLogLevel >= LOG_LEVEL_MAP.debug) {
      console.debug(...args);
    }
  },
};
